import * as CryptoJS from "crypto-js";

export function deleteAllCookies() {
  let cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    let eqPos = cookie.indexOf("=");
    let name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
}

export function getSortName(param: string): string {
  const name = param && param.split(" ");
  const sortName = name && name[0].charAt(0) + name[1].charAt(0);
  return sortName;
}

export function getDisplayName(param: string): string {
  return param && param.split(" ")[0];
}

export function stringToDate(str: any) {
  const [dd, mm, yyyy] = str.split("-");
  return new Date(yyyy, mm - 1, dd);
}

export function getFinancialYearsFrom2025(): object[] {
  const years: object[] = [];
  const startYear = 2025;
  const today = new Date();
  // Determine the current financial year start
  const currentFYStart =
    today.getMonth() + 1 <= 3 ? today.getFullYear() - 1 : today.getFullYear();
  for (let year = startYear; year <= currentFYStart; year++) {
    years.push({ label: `${year}-${year + 1}`, value: `${year}-${year + 1}` });
  }
  return years;
}
export function decryptId(
  token: string,
  secret: string = 'BadActorapplicationEncryptionKey'
): string {

  token = token.replace(/-/g, '+').replace(/_/g, '/');
  while (token.length % 4) token += '=';

  const combined = CryptoJS.enc.Base64.parse(token);

  const iv = CryptoJS.lib.WordArray.create(
    combined.words.slice(0, 4),
    16
  );

  const cipherText = CryptoJS.lib.WordArray.create(
    combined.words.slice(4),
    combined.sigBytes - 16
  );

  const key = CryptoJS.SHA256(secret);

  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: cipherText
  });

  const decrypted = CryptoJS.AES.decrypt(
    cipherParams,
    key,
    {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );

  return decrypted.toString(CryptoJS.enc.Utf8);
}

export const classificationReviewColumnDisplayNames: { [key: string]: string } = {
  objectId: "Object Id_IFS",
  objectDescription: "Object Description_IFS",
  terminalCode: "Reported Object Site_IFS",
  taskSeq: "Task No_IFS",
  workTypeId: "Work Type_IFS",
  downTimeType: "Down Time Type_IFS",
  downTime: "Down Time in Hours_IFS",
  cause: "Cause_AI",
  maintainableItem: "Maintainable Item_AI",
  failureMode: "Failure Mode_AI",
  performedWorkAI: "Performed Work_AI",
  explanationAI: "Explainable_AI",
  failureMechnism: "Failure Mechanism_AI",
  accuracyScore: "Accuracy_AI",
  reviewedOn: "Verified/Reviewed Date",
  recommendationsWeb: "Web Recommendations_AI",
};

export function getClassificationReviewColumnDisplayName(columnName: string, fallbackDisplayName: string): string {
  if (!columnName) return fallbackDisplayName;

  const normalizedColumnName = columnName.charAt(0).toLowerCase() + columnName.slice(1);
  return classificationReviewColumnDisplayNames[normalizedColumnName] || fallbackDisplayName;
}

export function normalizeClassificationReviewColumnDisplayNames(columns: any[] = []): any[] {
  return columns.map((col: any) => ({
    ...col,
    columnDisplayName: getClassificationReviewColumnDisplayName(col.columnName, col.columnDisplayName),
  }));
}


// Main table columns for Classification Review (Image 1 - ordered by business requirement)
export const classificationReviewMainColumns = [
  { columnDisplayName: "Object Id_IFS", columnName: "objectId", selected: true },
  { columnDisplayName: "Object Description_IFS", columnName: "objectDescription", selected: true },
  { columnDisplayName: "Concatenated Fields", columnName: "concatenatedFields", selected: true },
  { columnDisplayName: "Concatenated Translation", columnName: "concatenatedTranslation", selected: true },
  { columnDisplayName: "Maintainable Item_AI", columnName: "maintainableItem", selected: true },
  { columnDisplayName: "Failure Mechanism_AI", columnName: "failureMechnism", selected: true },
  { columnDisplayName: "Failure Mode_AI", columnName: "failureMode", selected: true },
  { columnDisplayName: "Performed Work_AI", columnName: "performedWorkAI", selected: true },
  { columnDisplayName: "Explainable_AI", columnName: "explanationAI", selected: true },
  { columnDisplayName: "Serial Batch ID", columnName: "batchSerialId", selected: true },
  { columnDisplayName: "Reported Object Site_IFS", columnName: "terminalCode", selected: true },
  { columnDisplayName: "Task No_IFS", columnName: "taskSeq", selected: true },
  { columnDisplayName: "Work Type_IFS", columnName: "workTypeId", selected: true },
  { columnDisplayName: "Down Time Type_IFS", columnName: "downTimeType", selected: true },
  { columnDisplayName: "Down Time in Hours_IFS", columnName: "downTime", selected: true },
  { columnDisplayName: "Status", columnName: "status", selected: true },
  { columnDisplayName: "Accuracy_AI", columnName: "accuracyScore", selected: true },
  { columnDisplayName: "Verified/Reviewed Date", columnName: "reviewedOn", selected: true },
  { columnDisplayName: "Accuracy Source", columnName: "accuracySource", selected: true },
];

// Canonical default sequence used whenever the user has not explicitly reordered columns
export const classificationReviewDefaultColumnOrder: string[] = classificationReviewMainColumns.map(
  (col) => col.columnName
);

export function sortByClassificationReviewDefaultOrder(columns: any[] = []): any[] {
  const orderIndex = new Map<string, number>(
    classificationReviewDefaultColumnOrder.map((name, index) => [name, index])
  );
  const rank = (col: any) => {
    const name: string = col?.columnName ?? "";
    const normalized = name ? name.charAt(0).toLowerCase() + name.slice(1) : name;
    return orderIndex.get(normalized) ?? Infinity;
  };
  return [...columns].sort((a: any, b: any) => rank(a) - rank(b));
}

// Details dropdown columns for Classification Review (Image 2 - shown on row expand)
export const classificationReviewDetailsColumns = [
  { columnDisplayName: "Web Recommendations_AI", columnName: "recommendationsWeb", selected: true },
];

// Main table + "More Details" columns that are pre-selected when the user has no saved preferences
export function isClassificationColumnSelectedByDefault(columnName: string): boolean {
  if (!columnName) return false;
  const normalized = columnName.charAt(0).toLowerCase() + columnName.slice(1);
  return [...classificationReviewMainColumns, ...classificationReviewDetailsColumns].some(
    (col: any) => col.columnName === normalized && col.selected
  );
}

// Old default columns - commented out as part of column rearrangement (DIG-11862)
// export const defaultClassificationColumns = [
//   { columnDisplayName: "Terminal", columnName: "terminalName", selected: true },
//   { columnDisplayName: "Object Id", columnName: "objectId", selected: true },
//   { columnDisplayName: "Object Description", columnName: "objectDescription", selected: true },
//   { columnDisplayName: "Task No", columnName: "taskSeq", selected: true },
//   { columnDisplayName: "Failure Mechanism", columnName: "failureMechnism", selected: true },
//   { columnDisplayName: "Fault Type Description IFS", columnName: "failureMechnismDescription", selected: true },
//   { columnDisplayName: "Cause", columnName: "cause", selected: true },
//   { columnDisplayName: "Cause Details IFS", columnName: "errorCauseDescription", selected: true },
//   { columnDisplayName: "Failure Mode", columnName: "failureMode", selected: true },
//   { columnDisplayName: "Maintainable Item", columnName: "maintainableItem", selected: true },
//   { columnDisplayName: "Maintenance Summary", columnName: "summary", selected: true },
//   { columnDisplayName: "Preffered Action Description", columnName: "performedActionDescription", selected: true },
//   { columnDisplayName: "Accuracy", columnName: "accuracyScore", selected: true },
//   { columnDisplayName: "Status", columnName: "status", selected: true },
//   { columnDisplayName: "Performed Work-AI", columnName: "performedWorkAI", selected: true },
//   { columnDisplayName: "Description", columnName: "workOrderDescription" },
//   { columnDisplayName: "Translated description in English", columnName: "translatedWorkOrderDescription" },
//   { columnDisplayName: "Work Type", columnName: "workTypeId" },
//   { columnDisplayName: "Reported Object Site", columnName: "terminalCode" },
//   { columnDisplayName: "Down Time in Hours", columnName: "downTime" },
//   { columnDisplayName: "Down Time Type", columnName: "downTimeType" },
//   { columnDisplayName: "Down Type Description", columnName: "downTimeTypeDescription" },
//   { columnDisplayName: "Long Description", columnName: "errorCauseLong" },
//   { columnDisplayName: "Translated Long Description in English", columnName: "translatedErrorCauseLong" },
//   { columnDisplayName: "Performed Work", columnName: "performedActionDescription" },
//   { columnDisplayName: "Translated Performed Work in English", columnName: "translatedPerformedWork" },
//   { columnDisplayName: "Action Taken", columnName: "actionTaken" },
//   { columnDisplayName: "Translated Action Taken in English", columnName: "translatedActionTaken" },
// ];
export const defaultClassificationColumns = classificationReviewMainColumns;
